import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { Rocket, BookOpen, Globe, Lightbulb, Handshake, Scale } from 'lucide-react';
import { supabase } from '../supabaseClient';
import './Careers.css';

const WHY_ITEMS = [
  { icon: <Rocket size={22} />, title: 'Cutting-Edge Technology', desc: 'Work with the latest AI technologies including GPT-4, LangChain, computer vision, and agentic AI systems.' },
  { icon: <BookOpen size={22} />, title: 'Continuous Learning', desc: 'Access to courses, conferences, and certifications. We invest in your growth and development.' },
  { icon: <Globe size={22} />, title: 'Remote-First Culture', desc: 'Work from anywhere with flexible hours. We focus on results, not where you sit.' },
  { icon: <Lightbulb size={22} />, title: 'Real Impact', desc: 'Build production systems used by real businesses. See your work make a difference.' },
  { icon: <Handshake size={22} />, title: 'Collaborative Environment', desc: 'Work with talented engineers who value knowledge sharing and teamwork.' },
  { icon: <Scale size={22} />, title: 'Work-Life Balance', desc: 'Reasonable hours, no crunch culture. We believe in sustainable productivity.' },
];

function JobSkeleton() {
  return (
    <div className="role-item skeleton-card" aria-hidden="true">
      <div className="role-info">
        <div className="skeleton-line" style={{ width: '55%', height: 18, marginBottom: '0.75rem' }} />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <div className="skeleton-line" style={{ width: 70, height: 22, borderRadius: 20 }} />
          <div className="skeleton-line" style={{ width: 80, height: 22, borderRadius: 20 }} />
          <div className="skeleton-line" style={{ width: 90, height: 22, borderRadius: 20 }} />
        </div>
      </div>
      <div className="skeleton-line" style={{ width: 100, height: 36, borderRadius: 8 }} />
    </div>
  );
}

function Careers() {
  usePageTitle('Careers');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('jobs')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setJobs(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="careers">
      <section className="careers-hero">
        <div className="careers-container">
          <h1 className="careers-hero-title">Join Our Team</h1>
          <p className="careers-hero-subtitle">Build the future of AI-powered software with us</p>
        </div>
      </section>

      <section className="careers-why">
        <div className="careers-container">
          <h2 className="section-title">Why Work at Biltzentech</h2>
          <div className="why-grid">
            {WHY_ITEMS.map((item, i) => (
              <div key={i} className="why-card">
                <div className="why-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="careers-culture">
        <div className="careers-container">
          <h2 className="section-title">Our Culture</h2>
          <div className="culture-content">
            <p>At Biltzentech, we're building more than software—we're building a team of passionate engineers who love solving complex problems. Our culture is built on:</p>
            <div className="culture-points">
              <div className="culture-point">
                <h3>Innovation &amp; Experimentation</h3>
                <p>We encourage trying new approaches and learning from failures. 20% time for personal projects and exploration.</p>
              </div>
              <div className="culture-point">
                <h3>Ownership &amp; Autonomy</h3>
                <p>You own your projects from conception to deployment. We trust you to make decisions.</p>
              </div>
              <div className="culture-point">
                <h3>Transparency &amp; Communication</h3>
                <p>Open communication across all levels. Regular team syncs, demos, and knowledge sharing sessions.</p>
              </div>
              <div className="culture-point">
                <h3>Diversity &amp; Inclusion</h3>
                <p>We're building a diverse team with different backgrounds, perspectives, and experiences.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="careers-roles">
        <div className="careers-container">
          <h2 className="section-title">Open Positions</h2>
          {loading ? (
            <div className="roles-list">
              {Array.from({ length: 3 }).map((_, i) => <JobSkeleton key={i} />)}
            </div>
          ) : jobs.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
              No open positions at the moment. Check back soon or send us your resume below.
            </p>
          ) : (
            <div className="roles-list">
              {jobs.map(job => (
                <div key={job.id} className="role-item">
                  <div className="role-info">
                    <h3>{job.title}</h3>
                    {job.description && <p>{job.description}</p>}
                    <div className="role-tags">
                      {job.type && <span>{job.type}</span>}
                      {job.location && <span>{job.location}</span>}
                      {job.experience && <span>{job.experience}</span>}
                    </div>
                  </div>
                  <Link to={`/job/${job.id}`} className="role-apply">Apply Now</Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="careers-cta">
        <div className="careers-container">
          <div className="cta-box">
            <h2>Don't See Your Role?</h2>
            <p>We're always looking for talented people. Send us your resume and let's talk.</p>
            <a href="mailto:careers@biltzentech.com" className="btn-primary">Send Your Resume</a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Careers;
